import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  TaskMutationParams,
  LeaderboardPeriodType,
} from "@/types/challenges.types";
import { usePlausible } from "./usePlausible";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { useEffect, useState, useCallback } from "react";
import { PulsarService } from "@/services/pulsar.service";
import { toast } from "sonner";
import { getErrorMessage } from "@/helpers/getErrorMessage";
import { consumeTaskGuidAndName } from "@/lib/challenges";
import { useAuth } from "./useAuth";
import { useTurnstile } from "@/contexts/turnstile-context";
import { CAMPAIGN_CONFIG } from "@/config";

const CHALLENGES_KEYS = {
  all: ["challenges"] as const,
  detail: (id: string, groupId: string) => ["challenges", id, groupId] as const,
  byGroup: (group: string) => ["challenges", "group", group] as const,
  taskStatus: (campaignId: string, groupId: string) =>
    ["challenges", "task-status", campaignId, groupId] as const,
  campaignUser: (campaignId: string, groupId: string) =>
    ["challenges", "user", campaignId, groupId] as const,
  campaignLeaderboard: (
    campaignId: string,
    groupId: string,
    limit: number,
    period: LeaderboardPeriodType = LeaderboardPeriodType.ALL
  ) =>
    ["challenges", "leaderboard", campaignId, groupId, limit, period] as const,
  validRevaAccount: (email: string) =>
    ["reva", "valid-account", email] as const,
};

export const useGetCampaigns = () => {
  return useQuery({
    queryKey: CHALLENGES_KEYS.all,
    queryFn: () => PulsarService.getChallengeCampaigns(),
  });
};

export const useGetAllCampaigns = (
  sortBy?: "participantsAsc" | "participantsDesc"
) => {
  return useQuery({
    queryKey: [CHALLENGES_KEYS.all, "all", sortBy],
    queryFn: async () => {
      const campaigns = await PulsarService.getAllCampaigns();
      const filteredCampaigns = campaigns.filter(
        (campaign: any) => campaign.isEnabled
      );

      if (sortBy === "participantsAsc") {
        return filteredCampaigns.sort(
          (a: any, b: any) => a.totalUsers - b.totalUsers
        );
      }

      if (sortBy === "participantsDesc") {
        return filteredCampaigns.sort(
          (a: any, b: any) => b.totalUsers - a.totalUsers
        );
      }

      const sortedCampaigns = filteredCampaigns
        .sort((a: any, b: any) => {
          const aEndDate = new Date(a.startDate);
          const bEndDate = new Date(b.startDate);
          return bEndDate.getTime() - aEndDate.getTime();
        })
        .filter((campaign: any) => campaign.isEnabled);

      return sortedCampaigns;
    },
  });
};

export const useGetAllCampaignsByGroup = (group: string) => {
  return useQuery({
    queryKey: CHALLENGES_KEYS.byGroup(group),
    queryFn: async () => {
      const campaigns = await PulsarService.getAllCampaignsByGroup(group);
      return campaigns ?? [];
    },
    enabled: !!group,
  });
};

export const useGetCampaign = (id: string, groupId: string | undefined) => {
  return useQuery({
    queryKey: CHALLENGES_KEYS.detail(id, groupId ?? ""),
    queryFn: () => PulsarService.getCampaign(id, groupId),
  });
};
export const useGetCampaignTaskStatus = (
  campaignId: string,
  groupId: string | undefined
) => {
  const { address, isAuthenticated, token } = useAuth();

  // Only enable when auth is fully ready (including token)
  const isEnabled = isAuthenticated && !!address && !!token;

  return useQuery({
    queryKey: CHALLENGES_KEYS.taskStatus(campaignId, groupId || ""),
    queryFn: () => {
      return PulsarService.getChallengeCampaignTaskStatus(campaignId, groupId);
    },
    enabled: isEnabled,
  });
};

export const useGetCampaignUser = (
  campaignId: string,
  groupId: string | undefined
) => {
  const { address, isAuthenticated, token } = useAuth();

  // Only enable when auth is fully ready (including token)
  const isEnabled = isAuthenticated && !!address && !!token;

  return useQuery({
    queryKey: CHALLENGES_KEYS.campaignUser(campaignId, groupId || ""),
    queryFn: async () => {
      return await PulsarService.getChallengeCampaignUser(campaignId, groupId);
    },
    enabled: isEnabled,
  });
};

export const useGetCampaignLeaderboard = (
  campaignId: string,
  groupId: string | undefined,
  _address: string,
  limit: number = 10,
  period: LeaderboardPeriodType = LeaderboardPeriodType.ALL,
  page: number = 0
) => {
  return useQuery({
    queryKey: [
      ...CHALLENGES_KEYS.campaignLeaderboard(
        campaignId,
        groupId || "",
        limit,
        period
      ),
      page,
    ],
    queryFn: async () => {
      let date;
      if (period === LeaderboardPeriodType.WEEKLY) {
        const today = new Date();
        const dayOfWeek = today.getDay() === 0 ? 7 : today.getDay();
        const diff = 1 - dayOfWeek;
        const monday = new Date(today);
        monday.setDate(today.getDate() + diff);
        date = monday.toISOString();
      }

      return await PulsarService.getChallengeCampaignLeaderboard(
        campaignId,
        groupId,
        page,
        limit,
        period,
        date
      );
    },
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 5,
  });
};

export const useQuestsMutations = () => {
  const queryClient = useQueryClient();
  const plausible = usePlausible();
  const { getToken } = useTurnstile();

  const taskMutation = useMutation({
    mutationFn: async ({
      taskGuid,
      extraArguments,
      campaignId,
      groupId,
    }: TaskMutationParams) => {
      try {
        // Get a fresh single-use Turnstile token before each request
        const turnstileToken = await getToken();

        const { data: result } = await PulsarService.doCampaignTask(
          taskGuid,
          extraArguments,
          turnstileToken
        );
        plausible("Airdrop: Do Task", {
          props: { taskGuid },
        });

        if (!result?.status) {
          toast.error(result.error || "Error doing task", {
            duration: 6000,
            dismissible: true,
            style: {
              background: "#fee2e2",
              color: "#991b1b",
              border: "1px solid #fca5a5"
            }
          });
          return null;
        }

        let updatedTaskStatus;
        try {
          updatedTaskStatus = await PulsarService.getChallengeCampaignTaskStatus(
            campaignId,
            groupId
          );

          queryClient.setQueryData(
            CHALLENGES_KEYS.taskStatus(campaignId, groupId || ""),
            updatedTaskStatus
          );
        } catch (refreshError) {
          console.warn(
            "[quests] Task completed but immediate status refresh failed",
            refreshError
          );
        }

        return { status: true, updatedTaskStatus, campaignId, groupId };
      } catch (e: any) {
        toast.error(getErrorMessage(e, "Error doing task"), {
          duration: 6000,
          dismissible: true,
          style: {
            background: "#fee2e2",
            color: "#991b1b",
            border: "1px solid #fca5a5"
          }
        });
        return null;
      }
    },
    onSuccess: (_) => {
      // if (data?.updatedTaskStatus) {
      //   // Immediately update the cache with the fetched data
      //   queryClient.setQueryData(
      //     CHALLENGES_KEYS.taskStatus(
      //       data.campaignId,
      //       data.groupId || "",
      //       "",
      //       false
      //     ),
      //     data.updatedTaskStatus
      //   );
      // }

      // Still invalidate queries to ensure consistency
      queryClient.invalidateQueries({
        queryKey: ["challenges", "task-status"],
      });
      queryClient.invalidateQueries({
        queryKey: ["challenges", "user"],
      });
      // Invalidate social user data to refresh social account linking state
      queryClient.invalidateQueries({
        queryKey: ["social-pay-user"],
      });
      // Invalidate leaderboard to show updated rankings
      queryClient.invalidateQueries({
        queryKey: ["challenges", "leaderboard"],
      });
    },
  });

  return {
    taskMutation,
  };
};

export const useActivateReferral = () => {
  const queryClient = useQueryClient();
  const { address } = useAuth();

  return useMutation({
    mutationFn: async (referralCode: string) => {
      try {
        const response = await PulsarService.createReferral(
          CAMPAIGN_CONFIG.id,
          CAMPAIGN_CONFIG.groupId,
          referralCode
        );

        toast.success("Referral activated successfully! 🎉", {
          duration: 4000,
          dismissible: true,
          style: {
            background: "#836FFF",
            color: "#FFFFFF",
            border: "1px solid rgba(131, 111, 255, 0.3)",
          },
        });

        return response.data;
      } catch (error: any) {
        const errorMessage = getErrorMessage(error, "Failed to activate referral code");
        toast.error(errorMessage, {
          duration: 6000,
          dismissible: true,
          style: {
            background: "#fee2e2",
            color: "#991b1b",
            border: "1px solid #fca5a5",
          },
        });
        throw error;
      }
    },
    onSuccess: () => {
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({
        queryKey: CHALLENGES_KEYS.campaignUser(CAMPAIGN_CONFIG.id, CAMPAIGN_CONFIG.groupId),
      });
      queryClient.invalidateQueries({
        queryKey: ["profile", address],
      });
    },
  });
};

export const useConsumeQuestsArgs = (
  campaignId: string,
  groupId: string | undefined
) => {
  const { taskMutation } = useQuestsMutations();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const getAndConsume = (key: string): string | null => {
    const value = searchParams?.get(key);
    if (!value) return null;

    const newSearchParams = new URLSearchParams(searchParams?.toString());
    newSearchParams.delete(key);

    const queryString = newSearchParams.toString();
    const newUrl = queryString ? `${pathname}?${queryString}` : pathname;
    router.replace(newUrl);
    return value;
  };

  const handleConsumeTwitterFollow = async () => {
    const value = getAndConsume("twitterAuth");
    if (!value) return;

    const { taskGuid, taskName } = consumeTaskGuidAndName();
    if (!taskGuid || !taskName) {
      toast.error("Task Guid not found", {
        style: {
          background: "#fee2e2",
          color: "#991b1b",
          border: "1px solid #fca5a5"
        }
      });
      return;
    }

    await taskMutation.mutateAsync({
      campaignId,
      groupId,
      taskName,
      taskGuid,
    });
  };

  const handleConsumeTwitterRelogin = async (
    campaignId: string,
    groupId: string | undefined
  ) => {
    const value = getAndConsume("twitterRelogin");
    if (!value) return;

    const { taskGuid, taskName } = consumeTaskGuidAndName();
    if (!taskGuid || !taskName) {
      toast.error("Task Guid not found", {
        style: {
          background: "#fee2e2",
          color: "#991b1b",
          border: "1px solid #fca5a5"
        }
      });
      return;
    }

    await taskMutation.mutateAsync({
      campaignId,
      groupId,
      taskName,
      taskGuid,
    });
  };

  const handleConsumeDiscordCode = async () => {
    const code = getAndConsume("code");
    if (!code) return;

    const { taskGuid, taskName } = consumeTaskGuidAndName();
    if (!taskGuid || !taskName) {
      toast.error("Task Guid not found", {
        style: {
          background: "#fee2e2",
          color: "#991b1b",
          border: "1px solid #fca5a5"
        }
      });
      return;
    }

    await taskMutation.mutateAsync({
      campaignId,
      groupId,
      taskName,
      extraArguments: [code],
      taskGuid,
    });
  };

  useEffect(() => {
    if (!campaignId) return;

    handleConsumeTwitterFollow();
    handleConsumeDiscordCode();
    handleConsumeTwitterRelogin(campaignId, groupId);
  }, [searchParams, campaignId, groupId]);
};

// Constants for localStorage keys
const REFERRAL_STORAGE_KEY = "pending_referral_code";

export const useReferral = (
  campaignId: string,
  groupId: string | undefined,
  address?: string,
  isAuthenticated: boolean = false
) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isOpen, setIsOpen] = useState(false);
  const handleOpen = () => setIsOpen(true);
  const handleClose = () => setIsOpen(false);
  const queryClient = useQueryClient();
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const plausible = usePlausible();

  // Get stored referral code
  const getStoredReferralCode = useCallback(() => {
    if (typeof window === "undefined") return null;
    try {
      const stored = localStorage.getItem(REFERRAL_STORAGE_KEY);
      if (!stored) return null;
      const { code, campaign } = JSON.parse(stored);
      return campaign === campaignId ? code : null;
    } catch {
      return null;
    }
  }, [campaignId]);

  // Store referral code
  const storeReferralCode = useCallback(
    (code: string) => {
      localStorage.setItem(
        REFERRAL_STORAGE_KEY,
        JSON.stringify({ code, campaign: campaignId })
      );
      setReferralCode(code);
    },
    [campaignId]
  );

  // Clear stored referral codechallenges.ts:509:43
  const clearStoredReferralCode = useCallback(() => {
    localStorage.removeItem(REFERRAL_STORAGE_KEY);
    setReferralCode(null);
  }, []);

  // Remove referral code from URL
  const removeReferralFromUrl = useCallback(() => {
    const newSearchParams = new URLSearchParams(searchParams?.toString());
    newSearchParams.delete("referralCode");
    const query = newSearchParams.toString();
    const newUrl = query ? `${pathname}?${query}` : pathname;
    router.replace(newUrl);
  }, [pathname, router, searchParams]);

  // Initialize referral code from URL or localStorage
  useEffect(() => {
    const urlReferralCode = searchParams?.get("referralCode");

    if (urlReferralCode) {
      storeReferralCode(urlReferralCode);
      // plausible("Referral: Open", {
      //   props: { referralCode: urlReferralCode },
      // });
    } else {
      const storedCode = getStoredReferralCode();
      if (storedCode) {
        setReferralCode(storedCode);
      }
    }
  }, [searchParams, getStoredReferralCode, storeReferralCode]);

  // Show modal when authenticated and have referral code
  useEffect(() => {
    if (isAuthenticated && address && referralCode) {
      handleOpen();
    }
  }, [isAuthenticated, referralCode, handleOpen]);

  const referralMutation = useMutation({
    mutationFn: async (code: string) => {
      await PulsarService.createReferral(campaignId, groupId, code);

      toast.success("Referral code applied successfully!");
      clearStoredReferralCode();
      removeReferralFromUrl();
      // Invalidate user data to reflect new referral status
      queryClient.invalidateQueries({
        queryKey: CHALLENGES_KEYS.campaignUser(campaignId, groupId || ""),
      });
      if (referralCode) {
        plausible("Referral: Apply", {
          props: { referralCode },
        });
      }
      handleClose();
    },
  });

  const handleCloseModal = useCallback(() => {
    removeReferralFromUrl();
    setReferralCode(null);
    clearStoredReferralCode();
    handleClose();
    if (referralCode) {
      plausible("Referral: Close", {
        props: { referralCode },
      });
    }
  }, [removeReferralFromUrl, handleClose, referralCode, plausible]);

  const handleReject = useCallback(() => {
    clearStoredReferralCode();
    removeReferralFromUrl();
    handleClose();
  }, [clearStoredReferralCode, removeReferralFromUrl, handleClose]);

  return {
    isOpen,
    onClose: handleCloseModal,
    onReject: handleReject,
    referralCode,
    applyReferral: referralMutation.mutateAsync,
    isLoading: referralMutation.isPending,
  };
};
